import isNil from 'lodash-es/isNil';
import * as yup from 'yup';
import { DeviceServiceAdmin } from '@hems/service';
import { FIRMWARE_UPDATE_API_RESPONSE_CODE } from '@hems/util/src/constant/firmwareUpdate';
import { isNull } from '@hems/util/src/helper/helper';
export const initYup = () => {
    yup.setLocale({
        mixed: {
            default: () => ({ key: 'message.invalid_value' }),
            required: ({ label }) => ({ key: 'message.field_required', values: { label } }),
        },
        string: {
            email: ({ label }) => ({ key: 'message.email_format', values: { label } }),
            min: ({ min, label }) => ({
                key: 'message.longer_than_min',
                values: { min, label },
            }),
            max: ({ max, label }) => ({
                key: 'message.shorter_than_max',
                values: { max, label },
            }),
        },
        number: {
            min: ({ min, label }) => ({
                key: 'message.greater_min',
                values: { min, label },
            }),
            max: ({ max, label }) => ({
                key: 'message.greater_max',
                values: { max, label },
            }),
            integer: ({ label }) => ({ key: 'message.none_integer', values: { label } }),
        },
    });
    yup.addMethod(yup.number, 'unitNumber', function addUnitNumberMethod(unitNumber, min = 0) {
        const getDecimalPoint = (num) => num?.toString().split('.')[1]?.length ?? 0;
        const exNumber = (unitNumber, min) => Array.from(Array(3), (_, i) => ((i + 1) * unitNumber + (min || 0)).toFixed(getDecimalPoint(unitNumber))).join(', ');
        return this.test('unitNumber', function validateUnitNumber(value) {
            const { createError } = this;
            if (isNil(value))
                return false;
            if (getDecimalPoint(value) <= getDecimalPoint(unitNumber))
                return true;
            return createError({
                message: {
                    key: 'message.insert_units',
                    values: {
                        unit_number: unitNumber,
                        ex: exNumber(unitNumber, min),
                    },
                },
            });
        });
    });
    yup.addMethod(yup.string, 'checkConfigurationOnAutoUpdate', function addCheckConfigurationOnAutoUpdateMethod(options) {
        return this.test('checkConfigurationOnAutoUpdate', async function checkConfigurationOnAutoUpdateTest(autoUpdate) {
            const { createError } = this;
            if (isNull(autoUpdate)) {
                return createError({
                    message: {
                        key: 'message.field_required',
                    },
                });
            }
            /** Auto Update가 아니라면, validation 체크를 할 필요가 없음 */
            if (autoUpdate === 'N')
                return true;
            try {
                const validateResult = await options.validator();
                if (validateResult.data.result === FIRMWARE_UPDATE_API_RESPONSE_CODE.DUPLICATE) {
                    return createError({
                        message: {
                            key: 'message.exist_stable_grp',
                            values: {
                                configuration: this.parent.systemConfiguration,
                            },
                        },
                    });
                }
                return true;
            }
            catch (e) {
                return false;
            }
        });
    });
    yup.addMethod(yup.string, 'groupName', function addGroupNameMethod(options) {
        return this.test('groupName', async function groupNameValidationTest(groupName) {
            const { createError } = this;
            if (isNull(groupName)) {
                return createError({
                    message: {
                        key: 'message.field_required',
                    },
                });
            }
            const regex = /^[a-zA-Z0-9.,+\s\-_]+$/;
            if (!regex.test(groupName)) {
                return createError({
                    message: {
                        key: 'message.invalid_special_character',
                    },
                });
            }
            try {
                const result = (await options.validator(groupName));
                if (result.data.result === FIRMWARE_UPDATE_API_RESPONSE_CODE.DUPLICATE) {
                    return createError({
                        message: {
                            key: 'message.duplicate_invalid_grp',
                        },
                    });
                }
                return true;
            }
            catch (e) {
                return false;
            }
        });
    });
    const prevValues = {};
    function clearPrevValues(name) {
        if (prevValues[name]) {
            delete prevValues[name];
        }
    }
    Object.assign(yup, { clearPrevValues });
    const isNilValueLatLon = (latitude, longitude, value) => isNil(value) || isNil(latitude) || isNil(longitude);
    const isSameLatLon = (latitude, longitude, { lat, lon }) => latitude === lat && longitude === lon;
    yup.addMethod(yup.string, 'isValidLatLon', function addIsValidLatLonMethod(prevValue, options, currValue) {
        return this.test('isValidLatLon', { key: 'message.duplicate_location' }, async function validateLatLon(value) {
            const { createError } = this;
            const latitude = this.parent[options.latKey || 'lat'];
            const longitude = this.parent[options.lonKey || 'lon'];
            if (isNilValueLatLon(latitude, longitude, value)) {
                return createError({
                    message: { key: 'message.field_required' },
                });
            }
            if (currValue && isSameLatLon(latitude, longitude, currValue.value)) {
                return currValue.status;
            }
            if (prevValue.value && isSameLatLon(latitude, longitude, prevValue.value)) {
                return prevValue.status;
            }
            try {
                const deviceService = new DeviceServiceAdmin(window.axiosInstance.axios);
                const result = await deviceService.checkDuplicatedLocation({ latitude, longitude });
                prevValue.status = !result;
                prevValue.value = { lat: latitude, lon: longitude };
                return !result;
            }
            catch (e) {
                return createError({
                    message: {
                        key: 'message.invalid',
                    },
                });
            }
        });
    });
    yup.addMethod(yup.string, 'isValidName', function addIsValidNameMethod() {
        return this.test('isValidName', function nameValidationTest(name) {
            const { createError } = this;
            if (isNull(name)) {
                return createError({
                    message: {
                        key: 'message.field_required',
                    },
                });
            }
            return true;
        });
    });
    yup.addMethod(yup.string, 'isValidTelephoneNumber', function addIsValidTelephoneNumberMethod() {
        return this.test('isValidTelephoneNumber', function telephoneNumberValidationTest(telephone) {
            const { createError } = this;
            if (isNull(telephone)) {
                return createError({
                    message: {
                        key: 'message.field_required',
                    },
                });
            }
            const regexp = /^\d{10}$/;
            if (!regexp.test(telephone)) {
                return createError({
                    message: {
                        key: 'message.invalid_telephone_10_digits',
                    },
                });
            }
            return true;
        });
    });
    yup.addMethod(yup.string, 'confirmTermination', function addConfirmTerminationMethod() {
        return this.test('confirmTermination', function confirmTerminationTest(telephone) {
            const { createError } = this;
            if (isNull(telephone)) {
                return createError({
                    message: {
                        key: 'message.field_required',
                    },
                });
            }
            const WITHDRAWAL_TEXT = 'DELETE';
            const regexp = new RegExp(`^${WITHDRAWAL_TEXT}$`);
            if (!regexp.test(telephone)) {
                return createError({
                    message: {
                        key: 'message.invalid',
                    },
                });
            }
            return true;
        });
    });
};
export const createInstallerIdConfirmTest = (params) => {
    return {
        name: 'installer-id-confirmed',
        test: (value, ctx) => {
            const current = (value || '').trim();
            const initial = (params.initialValue() || '').trim();
            const regex = params.emailRegex ?? /^[^\s@]+@[^\s@]+$/;
            if (current === initial || !regex.test(current))
                return true;
            if (!params.isConfirmed()) {
                return ctx.createError({
                    message: params.message,
                });
            }
            return true;
        },
    };
};
